// The order page's two shortcuts (parcel scanner plan, owner 2026-10-01):
// « Scanner » → the scanner page, « Aperçu 3D » → this order's 3D preview in a
// new tab. They live in ROW 1 of the sticky bar, beside the back button —
// never a row of their own: that bar already takes 190–270 px of a phone.
//
// 44 × 44 px targets with an icon over a short label, at every width (see
// TILE). A missing preview is a disabled control that says why — in its
// title, read out with it, and on a tap, since a phone shows no tooltip.
//
// Who sees them: the one action table (lib/actions.ts). Guard:
// OrderBarShortcuts.test.tsx.
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ScanLine } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useCan } from '@/lib/actions';

export interface PreviewLinkState {
  /** `reviewData.preview_url` — the same source as the « Aperçu client » card (section 4). */
  url: string | null | undefined;
  /** That read has not answered yet. */
  loading: boolean;
  /** That read failed. */
  error: boolean;
  /** The order has print PDFs — the previews are rasterised from them. */
  pdfsExist: boolean;
}

/** Why there is nothing to open. French: the string is the i18n key. */
export const PREVIEW_REASON = {
  loading: 'Recherche du lien d’aperçu…',
  error: 'Lien d’aperçu indisponible — rechargez la page.',
  noPdf: 'Pas encore d’aperçu : les PDF ne sont pas générés (étape 3).',
  noLink: 'Pas encore de lien d’aperçu : créez-le à l’étape 4 « Aperçu client ».',
} as const;

export type PreviewShortcut = { href: string; reason: null } | { href: null; reason: string };

/** Pure: the URL to open, or why there is none. A live link always opens. */
export function previewShortcut(s: PreviewLinkState): PreviewShortcut {
  if (s.url) return { href: s.url, reason: null };
  if (s.loading) return { href: null, reason: PREVIEW_REASON.loading };
  if (s.error) return { href: null, reason: PREVIEW_REASON.error };
  if (!s.pdfsExist) return { href: null, reason: PREVIEW_REASON.noPdf };
  return { href: null, reason: PREVIEW_REASON.noLink };
}

const HINT_MS = 4000;

// ⚠️ EXACTLY 44 × 44 px, AT EVERY WIDTH. Every pixel these take comes out of
// the order identity beside them: at 375 px a 51 px tile pushed the order
// facts onto a third line, and from md up (one wrapping line shared with the
// confirm button and the status controls) 215 px of pills crushed the identity
// to 70 px at 1280 px — both measured. The short label fits inside 44 px.
const TILE =
  'inline-flex flex-col items-center justify-center gap-0.5 h-11 w-11 px-0 '
  + 'rounded-xl border text-[9.5px] font-semibold leading-none tracking-tight whitespace-nowrap transition-colors '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';
const LIVE = `${TILE} bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900`;
const DEAD = `${TILE} bg-gray-50 text-gray-400 cursor-not-allowed`;
const BORDER = { borderColor: 'rgba(201,162,74,0.25)' };

export function OrderBarShortcuts({ preview }: { preview: PreviewLinkState }) {
  const { t } = useT();
  const can = useCan();
  const showScanner = can('order.open_scanner');
  const showPreview = can('order.open_preview');
  if (!showScanner && !showPreview) return null;

  return (
    <div className="shrink-0 flex items-center gap-1">
      {showScanner && (
        <Link to="/admin/scanner" className={LIVE} style={BORDER} title={t('Scanner les colis')}>
          <ScanLine className="h-4 w-4" aria-hidden />
          <span>{t('Scanner')}</span>
        </Link>
      )}
      {showPreview && <PreviewTile state={previewShortcut(preview)} />}
    </div>
  );
}

function PreviewTile({ state }: { state: PreviewShortcut }) {
  const { t } = useT();
  const reasonId = useId();
  const [hint, setHint] = useState(false);
  useEffect(() => {
    if (!hint) return;
    const id = window.setTimeout(() => setHint(false), HINT_MS);
    return () => window.clearTimeout(id);
  }, [hint]);

  // « Aperçu 3D » does not fit in 44 px: it is the control's name (aria-label)
  // and tooltip; « Aperçu » is what it shows.
  const face = (
    <>
      <BookOpen className="h-4 w-4" aria-hidden />
      <span>{t('Aperçu')}</span>
    </>
  );

  if (state.reason === null) {
    return (
      <a
        href={state.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('Aperçu 3D')}
        title={t('Ouvrir l’aperçu 3D du client dans un nouvel onglet')}
        className={LIVE}
        style={BORDER}
      >
        {face}
      </a>
    );
  }

  const reason = t(state.reason);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-disabled="true"
        aria-label={t('Aperçu 3D')}
        aria-describedby={reasonId}
        title={reason}
        onClick={() => setHint(true)}
        className={DEAD}
        style={BORDER}
      >
        {face}
      </button>
      <span id={reasonId} className="sr-only">{reason}</span>
      {/* Floats under the button: it must never add a line to the bar. */}
      {hint && (
        <span
          role="status"
          className="absolute right-0 top-full mt-1.5 z-40 w-max max-w-[220px] rounded-lg bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-lg"
        >
          {reason}
        </span>
      )}
    </span>
  );
}
